import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import compact from 'lodash/compact';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import { GET_ALL_MODULES } from 'graphql/admin/curriculum';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import SaveIcon from '@material-ui/icons/Save';
import { SortableContainer } from 'react-sortable-hoc';
import useSnack from 'hooks/useSnack';
import {
  GET_STUDENT_MILESTONES,
  UPSERT_STUDENT_MILESTONE,
} from 'graphql/admin/students';
import Spin from 'components/common/Spin';
import Milestone from './components/ScheduleMilestoneItem';
import ImportStudent from './components/ImportStudent';
import arrayMove from 'array-move';
import { useSelector } from 'react-redux';
import { GET_STATUSES } from 'graphql/student/stats';

const useStyles = makeStyles((theme) => ({
  root: {

  },
  card: {
    marginBottom: theme.spacing(2),
    maxWidth: 800,
    margin: 'auto'
  },
  cardFooter: {
    justifyContent: 'center',
  },
  footer: {
    background: 'rgb(248,248,248)',
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: 20,
    position: 'sticky',
    top: 0,
    textAlign: 'center',
    zIndex: 1,
  },
  fab: {
    position: 'fixed',
    bottom: 20,
    right: 100,
  },
  addContainer: {
    marginTop: theme.spacing(3),
  },
  add: {
    borderRadius: 20,
  },
  import: {
    borderRadius: 20,
  },

}));

const Schedule = ({ student, canEdit }) => {
  const classes = useStyles();
  const snack = useSnack();
  const client = useApolloClient();


  const { data } = useQuery(GET_ALL_MODULES, {
    fetchPolicy: 'cache-and-network'
  });

  const { data: creditData } = useQuery(GET_STATUSES, {
    variables: { userId: student.id, sub: student.sub },
    fetchPolicy: 'cache-and-network'
  })


  const [upsertData, { loading }] = useMutation(UPSERT_STUDENT_MILESTONE);
  const [milestones, setMilestones] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const removedMilestones = useRef([]);
  const removedTasks = useRef([]);




  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = () => {
    return client
      .query({
        query: GET_STUDENT_MILESTONES,
        variables: {
          userId: student.user_id,
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data: { student_milestones } }) => {
        setMilestones(
          student_milestones.map((m) => ({
            id: m.id,
            name: m.name,
            due_date: m.due_date,
            exercises: m.student_milestone_exercises.map((e) => ({
              id: e.id,
              module: e.exercise.module_id,
              exercise: e.exercise_id,
              name: e.exercise.name,
              type: e.exercise.meeting_type
            })),
          })),
        );
      });
  };

  const onAddMilestone = () => {
    milestones.push({
      name: '',
      exercises: [],
    });
    setMilestones([...milestones]);
  };

  const onAddExercise = (milestone, module, exercise) => {
    milestone.exercises.push({ module, exercise });
    setMilestones([...milestones]);
  };

  const onRemoveExercise = (milestone, ex) => {
    milestone.exercises = milestone.exercises.filter((e) => e !== ex);
    setMilestones([...milestones]);
    removedTasks.current.push(ex);
  };

  const onRemoveMilestone = (milestone) => {
    setMilestones(milestones.filter((m) => m !== milestone));
    removedMilestones.current.push(milestone);
  };

  const onUpdateMilestone = (milestone) => {
    setMilestones(milestones.map((m) => (m === milestone ? milestone : m)));
  };



  const handleSubmit = async () => {
    try {
      const milestoneIdsToRemove = compact(
        removedMilestones.current.map((i) => i.id),
      );
      const exerciseIdsToRemove = compact(
        removedTasks.current.map((i) => i.id),
      );
      const upsertMilestones = milestones.map((m, order) => {
        if (!m.name) {
          throw new Error('Please enter milestone name');
        }
        return {
          id: m.id,
          name: m.name,
          due_date: m.due_date,
          user_id: student.user_id,
          order,
          student_milestone_exercises: {
            data: m.exercises.map((i, idx) => ({
              id: i.id,
              exercise_id: i.exercise,
              user_id: student.user_id,
              order: idx,
            })),
            on_conflict: {
              constraint: 'student_milestone_tasks_pkey',
              update_columns: ['order'],
            },
          },
        };
      });

      await upsertData({
        variables: {
          upsertMilestones,
          deleteMilestones: milestoneIdsToRemove,
          deleteExercises: exerciseIdsToRemove,
        },
      });
      snack.success('Data saved.');
      removedMilestones.current = [];
      removedTasks.current = [];
      await fetchStudentData();
    } catch (e) {
      snack.error(e.message);
    }
  };

 

  const onMove = () => {
    setMilestones([...milestones]);
  };

  const onImport = () => {
    setShowImport(true);
  };

  const onImportData = (data) => {
    setShowImport(false);
    setMilestones([...milestones, ...data.data]);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const old = milestones[oldIndex];
    const _new = milestones[newIndex];
    old.order = newIndex;
    _new.order = oldIndex;
    setMilestones([...arrayMove(milestones, oldIndex, newIndex)]);
  };

  return (
    <div className={classes.root}>
      <Helmet title="Schedule" />
      <Typography gutterBottom variant="h4">
        Student curriculum
      </Typography>
      <br />
      {/*{!student.curriculum_id && (*/}
      {/*  <Alert severity="error">Student has no curriculum. Assign a curriculum first.</Alert>*/}
      {/*)}*/}
      {!data ? (
        <Spin spinning />
      ) : (
        <React.Fragment>
          {showImport && (
            <ImportStudent
              onImport={onImportData}
              onCancel={() => setShowImport(false)}
            />
          )}

          {!showImport && (
            <div className={classes.footer}>
              <Button
                onClick={onImport}
                startIcon={<ImportExportIcon />}
                variant="outlined"
                color="primary"
                className={classes.import}
              >
                Import from Template
              </Button>
              {' '}
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                color="primary"
                onClick={onAddMilestone}
                className={classes.add}
              >
                Add Milestone
              </Button>
            </div>
          )}
          {!showImport && (
            <SortableMilestones
              data={creditData?.credits}
              items={milestones}
              onMove={onMove}
              modules={data.modules}
              onRemoveExercise={onRemoveExercise}
              onRemoveMilestone={onRemoveMilestone}
              onAddExercise={onAddExercise}
              onUpdateMilestone={onUpdateMilestone}
              classes={classes}
              onSortEnd={onSortEnd}
              useDragHandle={true}
              canEdit={canEdit}
              lockAxis="y"
            />
          )}
          <div className={classes.fab}>
            <Tooltip title="Save" placement="left">
              <Fab
                size="large"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                <SaveIcon />
              </Fab>
            </Tooltip>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const SortableMilestones = SortableContainer(({ items, canEdit, ...other }) => {
  const user = useSelector((state) => state.user.user);

  return (
    <div>
      {items.map((m, i) => {
        return (
          <Milestone
            key={i}
            index={i}
            milestone={m}
            milestones={items}
            user={user}
            canEdit={canEdit}
            {...other}
          />
        );
      })}
    </div>
  );
});

export default SortableContainer(Schedule);
